import React, { useState } from 'react';
import {
    Container,
    Typography,
    Paper,
    Box,
    ToggleButtonGroup,
    ToggleButton,
    Link,
    Accordion,
    AccordionSummary,
    AccordionDetails,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';

const Support = () => {
    const [language, setLanguage] = useState('es');

    const handleLanguageChange = (event, newLanguage) => {
        if (newLanguage !== null) {
            setLanguage(newLanguage);
        }
    };

    const content = {
        es: {
            title: 'Soporte VideoCallShop',
            lastUpdated: 'Última actualización: 22 de abril de 2026',

            contactTitle: 'Cómo contactarnos',
            contactEmailLabel: 'Email de soporte:',
            contactEmail: 'soporte@videocallshop.com',
            hoursLabel: 'Horario de atención:',
            hours: 'Lunes a viernes de 9 a 18 (hora de Argentina).',

            faqTitle: 'Preguntas frecuentes',
            faqs: [
                {
                    q: '¿Cómo creo mi cuenta de tienda?',
                    a: 'Descargá la app VideoCallShop desde App Store o Google Play e iniciá el proceso de registro. Vas a necesitar un email válido, el nombre comercial de tu tienda y una contraseña. Una vez creada la cuenta, completá tu perfil comercial desde Configuración.',
                },
                {
                    q: '¿Cómo conecto Mercado Pago?',
                    a: 'Desde la app, andá a Configuración → Mercado Pago y tocá "Conectar cuenta". Se abrirá la página oficial de Mercado Pago para que autorices a VideoCallShop a recibir pagos en tu nombre. Una vez autorizado, volvés automáticamente a la app.',
                },
                {
                    q: '¿Cómo inicio una videollamada o transmisión en vivo?',
                    a: 'Desde la pantalla principal, tocá "Iniciar transmisión en vivo" para salir al aire, o respondé una solicitud de videollamada entrante desde la cola de llamadas. Antes de comenzar te pediremos permisos de cámara y micrófono.',
                },
                {
                    q: '¿Cómo elimino mi cuenta?',
                    a: 'Andá a Configuración → Eliminar cuenta dentro de la app. Vas a tener que confirmar con una palabra clave y la acción es irreversible: los datos personales se anonimizan de inmediato. Las transacciones pasadas se conservan por obligaciones fiscales (ver Política de Privacidad).',
                },
                {
                    q: 'Tengo problemas con el video o el audio, ¿qué hago?',
                    a: 'Revisá en la configuración del sistema que la app tenga permisos de cámara y micrófono activos. Si seguís sin video o audio, cerrá y volvé a abrir la app. Si el problema persiste, escribinos a soporte@videocallshop.com indicando tu modelo de dispositivo, sistema operativo y una descripción del problema.',
                },
            ],

            linksTitle: 'Enlaces útiles',
        },
        en: {
            title: 'VideoCallShop Support',
            lastUpdated: 'Last updated: April 22, 2026',

            contactTitle: 'How to contact us',
            contactEmailLabel: 'Support email:',
            contactEmail: 'soporte@videocallshop.com',
            hoursLabel: 'Support hours:',
            hours: 'Monday to Friday, 9:00 to 18:00 (Argentina time).',

            faqTitle: 'Frequently asked questions',
            faqs: [
                {
                    q: 'How do I create my store account?',
                    a: 'Download the VideoCallShop app from the App Store or Google Play and start the sign-up flow. You will need a valid email, your store name, and a password. Once the account is created, complete your business profile from Settings.',
                },
                {
                    q: 'How do I connect Mercado Pago?',
                    a: 'In the app, go to Settings → Mercado Pago and tap "Connect account". The official Mercado Pago page will open so you can authorize VideoCallShop to receive payments on your behalf. Once authorized, you will be returned to the app automatically.',
                },
                {
                    q: 'How do I start a video call or live broadcast?',
                    a: 'From the main screen, tap "Start live broadcast" to go live, or answer an incoming video call request from the call queue. Before starting, we will request camera and microphone permissions.',
                },
                {
                    q: 'How do I delete my account?',
                    a: 'Go to Settings → Delete account inside the app. You will need to confirm with a keyword and the action is irreversible: personal data is anonymized immediately. Past transactions are retained for tax obligations (see Privacy Policy).',
                },
                {
                    q: 'I am having video or audio problems, what do I do?',
                    a: 'Check in your system settings that the app has camera and microphone permissions enabled. If there is still no video or audio, close and reopen the app. If the problem persists, email soporte@videocallshop.com with your device model, operating system, and a description of the problem.',
                },
            ],

            linksTitle: 'Useful links',
        },
    };

    const t = content[language];

    return (
        <Container maxWidth="md" sx={{ py: 4 }}>
            <Paper elevation={3} sx={{ p: 4 }}>
                <Box
                    sx={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        mb: 3,
                        flexWrap: 'wrap',
                        gap: 2,
                    }}
                >
                    <Typography
                        variant="h4"
                        component="h1"
                        sx={{ fontWeight: 'bold', flex: 1, minWidth: '200px' }}
                    >
                        {t.title}
                    </Typography>
                    <ToggleButtonGroup
                        value={language}
                        exclusive
                        onChange={handleLanguageChange}
                        aria-label="language selection"
                        size="small"
                    >
                        <ToggleButton value="es" aria-label="español">
                            ES
                        </ToggleButton>
                        <ToggleButton value="en" aria-label="english">
                            EN
                        </ToggleButton>
                    </ToggleButtonGroup>
                </Box>

                <Typography variant="body2" color="text.secondary" sx={{ mb: 4 }}>
                    {t.lastUpdated}
                </Typography>

                <Typography variant="h5" component="h2" gutterBottom sx={{ mt: 2, mb: 2, fontWeight: 'bold' }}>
                    {t.contactTitle}
                </Typography>
                <Typography variant="body1" paragraph>
                    <strong>{t.contactEmailLabel}</strong>{' '}
                    <Link href={`mailto:${t.contactEmail}`}>{t.contactEmail}</Link>
                </Typography>
                <Typography variant="body1" paragraph>
                    <strong>{t.hoursLabel}</strong> {t.hours}
                </Typography>

                <Typography variant="h5" component="h2" gutterBottom sx={{ mt: 4, mb: 2, fontWeight: 'bold' }}>
                    {t.faqTitle}
                </Typography>
                {t.faqs.map((faq, index) => (
                    <Accordion key={index} disableGutters>
                        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                            <Typography variant="subtitle1" sx={{ fontWeight: 'bold' }}>
                                {faq.q}
                            </Typography>
                        </AccordionSummary>
                        <AccordionDetails>
                            <Typography variant="body1">{faq.a}</Typography>
                        </AccordionDetails>
                    </Accordion>
                ))}

                <Typography variant="h5" component="h2" gutterBottom sx={{ mt: 4, mb: 2, fontWeight: 'bold' }}>
                    {t.linksTitle}
                </Typography>
                <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                    <Link href="/privacy-policy">
                        {language === 'es' ? 'Política de Privacidad' : 'Privacy Policy'}
                    </Link>
                    <Link href="/terms">
                        {language === 'es' ? 'Términos y Condiciones' : 'Terms of Service'}
                    </Link>
                    <Link href="/community-guidelines">
                        {language === 'es' ? 'Normas de la Comunidad' : 'Community Guidelines'}
                    </Link>
                </Box>
            </Paper>
        </Container>
    );
};

export default Support;
